import { Chart as ChartJS, Filler, Legend, LineElement, PointElement, RadialLinearScale, Tooltip } from "chart.js";
import { t } from "i18next";
import { useState } from "react";
import { Radar } from "react-chartjs-2";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Arrow from "@/assets/icons/chevron.svg?react";
import nftGreen from "@/assets/images/nft-green.webp";
import nftGrey from "@/assets/images/nft-grey.webp";
import { Button } from "@/components/Button";
import { ColoredNetworkIconMap } from "@/constants/map";
import { useStore } from "@/hooks/store";
import { GraphData } from "@/interfaces/api";
import { Network } from "@/interfaces/network";
import { NFTNetworkProps } from "@/interfaces/nft";
import { APP_ROUTES } from "@/routes/constants";
import { hapiApi } from "@/services/api/hapi";
import { convertMillisecondsToDateParts } from "@/utils/date";

import styles from "./styles";
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export default function DashboardNetwork({
  network,
  score,
  nft,
}: {
  network: Network;
  score: {
    value: number;
    isMinted: boolean;
    expirationDate: number | null;
    top1000: number | null;
    rating: number | null;
  };
  nft: NFTNetworkProps | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [graph, setGraph] = useState<null | { labels: string[]; data: number[] }>(null);
  const navigate = useNavigate();
  const date = score.expirationDate ? convertMillisecondsToDateParts(score.expirationDate) : null;
  const hasNFT = Boolean(nft?.array.length);

  const { currentNetwork } = useStore((store) => ({
    currentNetwork: store.user.networksData[network],
  }));

  const formatGraphData = (graphData: GraphData) => {
    const labels = ["Achievements", "Transactions", "Name", "HAPI NFT", "Balance", "Age", "Accounts"];
    const data = [
      graphData.hot_achivments,
      graphData.total_transaction,
      graphData.wallet_name,
      graphData.nft,
      graphData.wallet_balance,
      graphData.wallet_age,
      graphData.unique_accounts,
    ];
    return { labels, data };
  };

  const openGraph = async () => {
    if (!graph && hasNFT) {
      const graphData = await hapiApi.getGraph(network, currentNetwork.accountId ?? "");

      if (graphData.data.errorCode === 0) {
        const newGraphData = formatGraphData(graphData.data.data);
        setGraph(newGraphData);
      }
    }
    setIsOpen(!isOpen);
  };

  const renderGraphData = (hasNFT: boolean, isOpen: boolean, network: Network) => {
    if (network !== Network.NEAR) return null;
    if (!hasNFT) {
      if (isOpen) {
        return (
          <styles.GetNftText>
            <p>{t("Airdrop.GetOn", { marketplace: "HOT Craft" })}</p>
            <Button
              name="BuyNFTOn"
              additionalDataForName={{ marketplace: "HOT Craft" }}
              handler={() => window.open("https://hotcraft.art/collection/hapi-nearonaut.near")}
            />
          </styles.GetNftText>
        );
      } else return null;
    }
    if (isOpen && graph)
      return (
        <styles.CanvasBorder>
          <Radar
            options={{
              scales: {
                r: {
                  pointLabels: { color: "black" },
                  ticks: { display: false },
                  max: 1,
                  min: 0,
                  grid: {
                    circular: false,
                    // lineWidth: (context) => (context.tick.value === 1 ? 1 : 0),
                  },
                  angleLines: {
                    display: false,
                  },
                },
              },
              plugins: {
                legend: { display: false },
                title: { display: false },
                tooltip: { enabled: false },
                decimation: { enabled: false },
              },
            }}
            data={{
              labels: graph.labels,
              datasets: [
                {
                  data: graph.data,
                  borderWidth: 1,
                  fill: true,
                  backgroundColor: "rgba(253, 132, 227, 0.3)",
                  borderColor: "#FD84E3",
                  pointBackgroundColor: "#FD84E3",
                },
              ],
            }}
          />
        </styles.CanvasBorder>
      );
  };

  return (
    <>
      <styles.Network key={network}>
        {ColoredNetworkIconMap[network]}
        <styles.Text>
          <p>{network}</p>
          {date && (
            <p>
              <Trans
                i18nKey="Dashboard.ExpiresDate"
                components={{ bold: <strong /> }}
                values={{ day: date.day, month: date.month, year: date.year }}
              />
            </p>
          )}
        </styles.Text>
        <styles.Nft $hasNFT={hasNFT} onClick={() => navigate(APP_ROUTES.AIRDROPS)}>
          {hasNFT ? <img src={nftGreen} alt="available nft" /> : <img src={nftGrey} alt="nft" />}
        </styles.Nft>
        <styles.ScoreAmount>
          <span>{score.value || "-"}</span>
          <span>/100</span>
        </styles.ScoreAmount>
        {network === Network.NEAR ? (
          <styles.Arrow $isOpen={isOpen} onClick={openGraph}>
            <Arrow />
          </styles.Arrow>
        ) : null}
      </styles.Network>
      {renderGraphData(hasNFT, isOpen, network)}
    </>
  );
}
